import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FileText, Trash2, Upload } from "lucide-react"
import { type ChangeEvent, useRef, useState } from "react"
import { api, ErrorApi, pedir } from "@/api/cliente"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * Documentos del expediente: subida, tabla y retirada.
 *
 * La subida va como `multipart/form-data`, así que **no** pasa por `api.post`, que
 * fija `Content-Type: application/json`. `pedir` con un `FormData` deja que el
 * navegador ponga el suyo con el `boundary` — escribirlo a mano lo rompe.
 *
 * El borrado es **suave**: la fila sigue siendo evidencia de que ese documento se
 * cargó, con su `sha256`. Lo que cambia es que deja de satisfacer la compuerta.
 */

export interface Documento {
  id: number
  expediente_id: number
  tipo_documento: string
  nombre_original: string
  mime: string
  bytes: number
  sha256: string
  visible_cliente: boolean
  subido_por: number
  creado_en: string
}

/** Los tipos que el seed declara obligatorios, más los habituales. */
const TIPOS = ["BL", "FACTURA_COMERCIAL", "DUA", "PLANILLA_LIQUIDACION", "OTRO"] as const
type TipoDocumento = (typeof TIPOS)[number]

function kilobytes(bytes: number): string {
  return `${(bytes / 1024).toFixed(0)} KB`
}

export function PanelDocumentos({ expedienteId }: { expedienteId: number }) {
  // Literal, no `TIPOS[0]`: con `noUncheckedIndexedAccess` un acceso indexado es
  // `string | undefined`, y `FormData.append` no lo acepta.
  const [tipo, setTipo] = useState<TipoDocumento>("BL")
  const entrada = useRef<HTMLInputElement>(null)
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()

  const documentos = useQuery({
    queryKey: ["documentos", expedienteId],
    queryFn: () => api.get<Documento[]>(`/api/v1/expedientes/${expedienteId}/documentos`),
  })

  function invalidar() {
    clienteQuery.invalidateQueries({ queryKey: ["documentos", expedienteId] })
    // La compuerta depende de estos documentos: sus destinos posibles cambian.
    clienteQuery.invalidateQueries({ queryKey: ["transiciones-posibles", expedienteId] })
  }

  const subir = useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData()
      cuerpo.append("archivo", archivo)
      cuerpo.append("tipo_documento", tipo)
      return pedir<Documento>(`/api/v1/expedientes/${expedienteId}/documentos`, {
        method: "POST",
        body: cuerpo,
      })
    },
    onSuccess: () => {
      invalidar()
      if (entrada.current) entrada.current.value = ""
    },
  })

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/documentos/${id}`),
    onSuccess: invalidar,
  })

  function alElegir(evento: ChangeEvent<HTMLInputElement>) {
    const archivo = evento.target.files?.[0]
    if (archivo) subir.mutate(archivo)
  }

  return (
    <div>
      {tienePermiso(usuario, "documento:subir") && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="tipo-doc" className="mb-1 block text-xs font-medium uppercase">
              Tipo de documento
            </label>
            <select
              id="tipo-doc"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoDocumento)}
              className="h-9 rounded-[var(--radius-control)] border px-2 text-sm"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--surface)",
                color: "var(--fg)",
              }}
            >
              {TIPOS.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </div>

          <label
            className="flex h-9 cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border px-3 text-sm"
            style={{ borderColor: "var(--border)" }}
          >
            <Upload size={16} aria-hidden="true" />
            {subir.isPending ? "Subiendo…" : "Elegir archivo"}
            {/* Se aceptan sólo los tipos que el servidor admite. Es una
                comodidad, no una defensa: el servidor comprueba los magic bytes. */}
            <input
              ref={entrada}
              type="file"
              className="sr-only"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={alElegir}
              disabled={subir.isPending}
            />
          </label>
        </div>
      )}

      {subir.isError && (
        <p role="alert" className="mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          {subir.error instanceof ErrorApi ? subir.error.message : "No se pudo subir el archivo"}
        </p>
      )}

      {documentos.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando documentos…
        </p>
      )}

      {documentos.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudieron cargar los documentos
        </p>
      )}

      {documentos.data?.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 superficie py-10"
          style={{ color: "var(--fg-muted)" }}
        >
          <FileText size={22} aria-hidden="true" />
          <p className="text-sm">Este expediente no tiene documentos cargados</p>
        </div>
      )}

      {documentos.data && documentos.data.length > 0 && (
        <Tabla>
          <Cabecera>
            <Th>Tipo</Th>
            <Th>Archivo</Th>
            <Th derecha>Tamaño</Th>
            <Th derecha>Cargado</Th>
            <Th />
          </Cabecera>
          <Cuerpo>
            {documentos.data.map((doc) => (
              <Fila key={doc.id}>
                <Td className="font-medium">{doc.tipo_documento.replaceAll("_", " ")}</Td>
                <Td>
                  {/* Descarga por el endpoint autenticado. `storage/` no se
                        sirve como estático nunca. */}
                  <a
                    href={`/api/v1/documentos/${doc.id}/contenido`}
                    className="underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {doc.nombre_original}
                  </a>
                </Td>
                <Td derecha numero>
                  {kilobytes(doc.bytes)}
                </Td>
                <Td derecha numero style={{ color: "var(--fg-muted)" }}>
                  {comoFecha(doc.creado_en)}
                </Td>
                <Td derecha>
                  {tienePermiso(usuario, "documento:eliminar") && (
                    <button
                      type="button"
                      onClick={() => eliminar.mutate(doc.id)}
                      disabled={eliminar.isPending}
                      aria-label={`Retirar ${doc.nombre_original}`}
                      className="rounded-[var(--radius-control)] p-1.5 disabled:opacity-40"
                      style={{ color: "var(--destructive)" }}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  )}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      )}
    </div>
  )
}
